import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertOctagon, AlertTriangle, Clock, ChevronRight, MessageCircle } from "lucide-react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import WhatsAppReminderDialog from "@/components/WhatsAppReminderDialog";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Compact banner shown at the top of the Dashboard.
 * Displays 3 colored cards with counts of policies grouped by urgency:
 *   - Expired (red)
 *   - Expiring within 7 days (orange)
 *   - Expiring within 30 days (amber)
 * Clicking a card opens an inline list of the affected policies with a link to the client.
 */
export default function PolicyRemindersBanner() {
  const { user } = useAuth();
  const [data, setData] = useState({ expired: [], in_7d: [], in_30d: [], counts: { expired: 0, in_7d: 0, in_30d: 0 } });
  const [openKey, setOpenKey] = useState(null); // expired | in_7d | in_30d | null
  const [loading, setLoading] = useState(true);
  const [waTarget, setWaTarget] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // Idempotently sync auto-tasks for policies expiring in <=30 days, then refresh banner data
        const sync = await api.post("/policies/reminders/sync-tasks").catch(() => ({ data: { created: 0 } }));
        if (alive && sync?.data?.created > 0) {
          toast.success(`${sync.data.created} Erinnerungs-Aufgabe(n) automatisch erstellt`);
        }
        const r = await api.get("/policies/reminders");
        if (alive) setData(r.data);
      } catch { /* */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const total = (data.counts?.expired || 0) + (data.counts?.in_7d || 0) + (data.counts?.in_30d || 0);
  if (loading || total === 0) return null;

  const cards = [
    {
      key: "expired",
      label: "Abgelaufen",
      sub: "Sofort handeln",
      count: data.counts.expired,
      icon: AlertOctagon,
      tone: "bg-red-600 text-white border-red-700",
      iconTone: "text-white/90",
      ringTone: "ring-red-500/30",
      items: data.expired,
    },
    {
      key: "in_7d",
      label: "Läuft in 7 Tagen ab",
      sub: "Dringlich",
      count: data.counts.in_7d,
      icon: AlertTriangle,
      tone: "bg-amber-500 text-white border-amber-600",
      iconTone: "text-white/90",
      ringTone: "ring-amber-400/30",
      items: data.in_7d,
    },
    {
      key: "in_30d",
      label: "Läuft in 30 Tagen ab",
      sub: "Vorbereiten",
      count: data.counts.in_30d,
      icon: Clock,
      tone: "bg-foreground/95 text-background border-foreground",
      iconTone: "text-background/90",
      ringTone: "ring-foreground/20",
      items: data.in_30d,
    },
  ];

  const visible = cards.filter(c => c.count > 0);
  const opened = visible.find(c => c.key === openKey);

  return (
    <div className="prime-card p-4 sm:p-5 border-l-4 border-l-red-600" data-testid="policy-reminders-banner">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertOctagon className="w-4 h-4 text-red-600" strokeWidth={1.7} />
          <h3 className="font-display font-bold text-sm uppercase tracking-widest">Police-Mahnungen</h3>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{total} Policen betroffen</span>
      </div>

      <div className={`grid grid-cols-1 sm:grid-cols-${visible.length === 1 ? "1" : visible.length === 2 ? "2" : "3"} gap-3`}>
        {visible.map((c) => {
          const Icon = c.icon;
          const isOpen = openKey === c.key;
          return (
            <button
              key={c.key}
              onClick={() => setOpenKey(isOpen ? null : c.key)}
              className={`text-left rounded-lg border-2 p-4 transition-all ${c.tone} ${isOpen ? `ring-4 ${c.ringTone}` : "hover:scale-[1.01]"}`}
              data-testid={`reminder-card-${c.key}`}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{c.sub}</div>
                  <div className="font-display font-extrabold text-3xl mt-1 leading-none">{c.count}</div>
                  <div className="text-sm font-medium mt-1.5">{c.label}</div>
                </div>
                <Icon className={`w-5 h-5 ${c.iconTone}`} strokeWidth={1.5} />
              </div>
            </button>
          );
        })}
      </div>

      {opened && (
        <div className="mt-4 rounded-md border border-border bg-card divide-y divide-border max-h-72 overflow-y-auto" data-testid={`reminder-list-${opened.key}`}>
          {opened.items.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary group"
              data-testid={`reminder-item-${p.id}`}
            >
              <div className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full shrink-0 ${
                p.days < 0 ? "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300"
                : p.days <= 7 ? "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                : "bg-muted text-foreground"
              }`}>
                {p.days < 0 ? `−${Math.abs(p.days)} T` : `${p.days} T`}
              </div>
              <Link to={`/clients/${p.client_id}`} className="flex-1 min-w-0 hover:underline">
                <div className="text-sm font-medium truncate">{p.company} <span className="text-muted-foreground font-mono text-xs">· {p.policy_number}</span></div>
                <div className="text-xs text-muted-foreground truncate">{p.client_name} · {fmtDate(p.end_date)}</div>
              </Link>
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    const c = await api.get(`/clients/${p.client_id}`);
                    setWaTarget({ client: c.data, policy: { id: p.id, company: p.company, policy_number: p.policy_number, insurance_type: p.insurance_type, end_date: p.end_date } });
                  } catch {
                    toast.error("Kunde nicht gefunden");
                  }
                }}
                title="WhatsApp-Erinnerung senden"
                className="inline-flex items-center justify-center w-8 h-8 rounded-md text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30 transition-colors shrink-0"
                data-testid={`reminder-wa-${p.id}`}
              >
                <MessageCircle className="w-4 h-4" strokeWidth={1.7} />
              </button>
              <Link to={`/clients/${p.client_id}`} className="text-muted-foreground hover:text-prime shrink-0">
                <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          ))}
        </div>
      )}

      <WhatsAppReminderDialog
        open={!!waTarget}
        onOpenChange={() => setWaTarget(null)}
        client={waTarget?.client}
        policy={waTarget?.policy}
        advisorName={user?.full_name}
      />
    </div>
  );
}
